import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Book, Users, MapPin, Crown, Hand, Eye, Heart, CheckCircle, Sparkles } from 'lucide-react';

const StudyGuide = ({ sermonId, onClose }) => {
  const [expandedSections, setExpandedSections] = useState(new Set(['overview']));
  const [completedSections, setCompletedSections] = useState(new Set());

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const markComplete = (sectionId) => {
    const newCompleted = new Set(completedSections);
    newCompleted.add(sectionId);
    setCompletedSections(newCompleted);
  };

  const StudySection = ({ id, title, children }) => {
    const isExpanded = expandedSections.has(id);
    const isCompleted = completedSections.has(id);
    return (
      <div className="border border-gray-600 rounded-lg mb-4" data-testid={`study-section-${id}`}>
        <button
          onClick={() => toggleSection(id)}
          className="w-full px-6 py-4 flex items-center justify-between bg-gray-800 hover:bg-gray-700 transition-colors rounded-t-lg"
        >
          <div className="flex items-center space-x-3">
            {isCompleted ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <div className="w-5 h-5 border-2 border-gray-500 rounded-full" />
            )}
            <h3 className="text-lg font-semibold text-white text-left">{title}</h3>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-yellow-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-yellow-400" />
          )}
        </button>
        {isExpanded && (
          <div className="px-6 py-4 bg-gray-900">
            {children}
            <button
              onClick={() => markComplete(id)}
              className="mt-4 bg-yellow-400 text-black px-4 py-2 rounded-full font-semibold hover:bg-yellow-300 transition-colors"
            >
              Mark Complete
            </button>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // SERMON 1 — He Waits at the Gates (Psalm 100:4)
  // ============================================
  const renderHeWaitsAtTheGates = () => {
    const templeData = [
      {
        id: 'outer-court',
        title: 'The Outer Court: The Messy Place',
        theme: 'Sacrifice & Surrender',
        description: 'This area was not glamorized because it was messy: Animals, knives, blood, burning flesh, smoke.',
        principle: 'Before something HOLY can live, something FLESHLY must die.',
        scripture: 'Leviticus 1:1-17',
        application: 'Real worship calls for death: It breaks pride, ego, and flesh. Sometimes worship isn\'t "churchy"—thankfulness can pour out with tears.',
        icon: <Users className="w-6 h-6 text-yellow-400" />
      },
      {
        id: 'holy-place',
        title: 'The Holy Place: Priestly Responsibility',
        theme: 'Lifestyle Worship',
        description: 'The place of Priestly responsibility with three significant artifacts:',
        artifacts: [
          { name: 'The Table of Showbread (12 pieces)', meaning: 'ALWAYS present - The Word of God', reference: 'Leviticus 24:5-9' },
          { name: 'The Altar of Incense', meaning: 'ALWAYS rising - Prayer', reference: 'Exodus 30:1-10' },
          { name: 'The Golden Lampstand (Menorah)', meaning: 'ALWAYS burning - The Holy Spirit', reference: 'Exodus 25:31-40' }
        ],
        principle: 'Worship is not a MOMENT... Worship is a LIFESTYLE.',
        scripture: '1 Samuel 3:1-18',
        application: 'The lamp of God was going out, but before God allowed His work to go undone, He raised up a prophet (Samuel).',
        icon: <Book className="w-6 h-6 text-yellow-400" />
      },
      {
        id: 'holy-of-holies',
        title: 'The Holy of Holies: Behind the Veil',
        theme: 'Atonement & Access',
        description: 'The Veil was thick, 4 stories tall, heavy, and holy material.',
        principle: 'Only one man (The High Priest) could enter, one day a year (Yom Kippur), with one purpose: THE BLOOD!',
        scripture: 'Leviticus 16:2, 14, 29-30; Hebrews 9:12',
        application: 'The Blood was to cover, purge, reconcile, propitiate, and ransom.',
        order: ['Before glory comes sacrifice', 'Before presence comes payment', 'Before worship comes atonement'],
        icon: <Crown className="w-6 h-6 text-yellow-400" />
      }
    ];

    const encounterData = [
      { person: 'Adam', location: 'The Garden', reference: 'Genesis 3:8-9' },
      { person: 'Noah', location: 'The Ark', reference: 'Genesis 8:15' },
      { person: 'Abraham', location: 'A Tent', reference: 'Genesis 18:1' },
      { person: 'Jacob', location: 'River Jabbok', reference: 'Genesis 32:22-30' },
      { person: 'Moses', location: 'Mountain Horeb', reference: 'Exodus 3:1-2' },
      { person: 'Joshua', location: 'Outskirts of Jericho', reference: 'Joshua 5:13-15' },
      { person: 'Elijah', location: 'A Cave (Horeb)', reference: '1 Kings 19:9-13' },
      { person: 'Isaiah', location: 'In the Temple', reference: 'Isaiah 6:1-8' },
      { person: 'Jeremiah', location: 'Quiet Time', reference: 'Jeremiah 1:4-10' },
      { person: 'Ezekiel', location: 'River Chebar', reference: 'Ezekiel 1:1-3' },
      { person: 'Daniel', location: 'The Bedroom/Lions Den', reference: 'Daniel 7:1; 6:10-23' },
      { person: 'Zacharias', location: 'Temple Sanctuary', reference: 'Luke 1:8-13' },
      { person: 'Mary', location: 'Home in Nazareth', reference: 'Luke 1:26-38' },
      { person: 'Peter, James & John', location: 'Mount of Transfiguration', reference: 'Matthew 17:1-8' },
      { person: 'Saul (Paul)', location: 'Road to Damascus', reference: 'Acts 9:1-6' },
      { person: 'John', location: 'Island of Patmos', reference: 'Revelation 1:9-10' },
      { person: 'Jesus', location: 'Garden of Gethsemane', reference: 'Matthew 26:36' }
    ];

    return (
      <>
        <StudySection id="overview" title="Scripture Foundation & Command">
          <div className="space-y-4">
            <div className="bg-yellow-400 text-black p-4 rounded-lg">
              <h4 className="font-bold mb-2">Psalm 100:4</h4>
              <p className="italic">"Enter into his gates with thanksgiving, and into his courts with praise: be thankful unto him, and bless his name."</p>
            </div>
            <p className="text-gray-300">
              <strong className="text-yellow-400">This is not a suggestion, but a command</strong> and a pattern for approaching God.
              The Psalmist describes the entrance into the Old Testament Temple, the place of God's visible glory,
              providing a blueprint for how we approach God today.
            </p>
            <div className="bg-gray-800 p-4 rounded-lg">
              <h5 className="text-yellow-400 font-semibold mb-2">Discussion Question:</h5>
              <p className="text-gray-300">What is the difference between thanksgiving (a deliberate action) and praise (an active response) in your personal life?</p>
            </div>
          </div>
        </StudySection>

        <StudySection id="temple-layers" title="The Temple: Layers of Access">
          <div className="space-y-6">
            {templeData.map((layer) => (
              <div key={layer.id} className="border border-gray-700 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  {layer.icon}
                  <h4 className="text-yellow-400 font-bold text-lg">{layer.title}</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-300 mb-2"><strong>Theme:</strong> {layer.theme}</p>
                    <p className="text-gray-300 mb-3">{layer.description}</p>
                    {layer.artifacts && (
                      <div className="mb-3">
                        <p className="text-yellow-400 font-semibold mb-2">Sacred Artifacts:</p>
                        <ul className="space-y-1">
                          {layer.artifacts.map((artifact, index) => (
                            <li key={index} className="text-sm text-gray-300">
                              <strong>{artifact.name}:</strong> {artifact.meaning} ({artifact.reference})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-yellow-400 font-semibold mb-2">Spiritual Principle:</p>
                    <p className="text-gray-300 mb-3 italic">"{layer.principle}"</p>
                    <p className="text-gray-400 text-sm mb-2"><strong>Scripture:</strong> {layer.scripture}</p>
                    <p className="text-gray-300 text-sm">{layer.application}</p>
                    {layer.order && (
                      <div className="mt-3">
                        <p className="text-yellow-400 font-semibold mb-2">The Order of Approach:</p>
                        <ul className="space-y-1">
                          {layer.order.map((item, index) => (
                            <li key={index} className="text-sm text-gray-300">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </StudySection>

        <StudySection id="the-gate" title="The Gate: Where God Waits">
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-purple-900 to-blue-800 p-4 rounded-lg">
              <h4 className="text-yellow-400 font-bold mb-2">The Key Command</h4>
              <p className="text-white mb-2">Before the Outer Court, the Inner Court, or the Veil, there is the GATE.</p>
              <p className="text-yellow-400 font-semibold">HE says: "Enter His gates with THANKSGIVING..." (Psalm 100:4)</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-800 p-4 rounded-lg text-center">
                <h5 className="text-yellow-400 font-bold mb-2">PASSWORD</h5>
                <p className="text-gray-300">Thanksgiving is the key to get into His presence</p>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg text-center">
                <h5 className="text-yellow-400 font-bold mb-2">ENTRY CODE</h5>
                <p className="text-gray-300">Required pattern for approaching God</p>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg text-center">
                <h5 className="text-yellow-400 font-bold mb-2">WAITING PLACE</h5>
                <p className="text-gray-300">"God's waiting... at the GATE"</p>
              </div>
            </div>
          </div>
        </StudySection>

        <StudySection id="private-encounters" title="God Always Meets in Private Places">
          <div className="space-y-4">
            <p className="text-gray-300 mb-4">God initiates encounters where He can speak personally. He chooses specific, private locations for divine appointments.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {encounterData.map((encounter, index) => (
                <div key={index} className="bg-gray-800 p-3 rounded-lg">
                  <div className="flex items-center space-x-2 mb-1">
                    <MapPin size={16} className="text-yellow-400 flex-shrink-0" />
                    <h5 className="font-semibold text-white text-sm">{encounter.person}</h5>
                  </div>
                  <p className="text-gray-300 text-sm mb-1">{encounter.location}</p>
                  <p className="text-gray-400 text-xs">{encounter.reference}</p>
                </div>
              ))}
            </div>
            <div className="bg-yellow-400 text-black p-4 rounded-lg">
              <h5 className="font-bold mb-2">Reflection Prompt:</h5>
              <p>Where is the "private place" (or the "Gate") that God is currently meeting you in your life? How can your Thanksgiving open that door?</p>
            </div>
          </div>
        </StudySection>

        <StudySection id="application" title="Personal Application & Next Steps">
          <div className="space-y-4">
            <div className="bg-gray-800 p-4 rounded-lg">
              <h5 className="text-yellow-400 font-bold mb-3">This Week's Challenge:</h5>
              <ul className="space-y-2 text-gray-300">
                <li>• Begin each day by "entering His gates with thanksgiving" - name 3 specific things you're grateful for</li>
                <li>• Identify your personal "gate" - where is God meeting you privately right now?</li>
                <li>• Practice lifestyle worship: Keep the "lampstand burning", "bread present", and "incense rising"</li>
                <li>• Remember: Thanksgiving is not just a feeling, it's a deliberate action and the key to God's presence</li>
              </ul>
            </div>
            <div className="bg-yellow-400 text-black p-4 rounded-lg">
              <h5 className="font-bold mb-2">Prayer to Close:</h5>
              <p className="italic">"Lord, thank You for waiting at the gates of my heart. I choose to approach You with intentional thanksgiving. Help me to surrender my flesh in the outer court, live as a priest in daily worship, and walk confidently through the torn veil into Your presence. Thank You that the way is open through Jesus. Amen."</p>
            </div>
          </div>
        </StudySection>
      </>
    );
  };

  // ============================================
  // SERMON 2 — The Same Hand (Mark 8:22-25)
  // ============================================
  const renderTheSameHand = () => {
    const twoTouches = [
      {
        id: 't1',
        label: 'The First Touch',
        what: 'Jesus took him by the hand, led him OUT of the town, spit on his eyes, and laid hands on him.',
        result: 'The man said: "I see men as trees walking."',
        revelation: 'The first touch released VISION — the ability to perceive, but not yet with clarity.',
        verse: 'Mark 8:23-24',
        icon: <Eye className="w-6 h-6 text-yellow-400" />
      },
      {
        id: 't2',
        label: 'The Second Touch',
        what: 'Jesus put His hands again on his eyes and made him look up.',
        result: 'He was restored and saw every man clearly.',
        revelation: 'The second touch released SIGHT — full restoration and clarity. The SAME HAND that gave vision gave sight.',
        verse: 'Mark 8:25',
        icon: <Sparkles className="w-6 h-6 text-yellow-400" />
      }
    ];

    const sameHandTimeline = [
      { id: 'h1', moment: 'Formed Adam from the dust of the ground', reference: 'Genesis 2:7' },
      { id: 'h2', moment: 'Held the ram for Abraham on Mount Moriah', reference: 'Genesis 22:13' },
      { id: 'h3', moment: 'Wrestled with Jacob and renamed him Israel', reference: 'Genesis 32:24-28' },
      { id: 'h4', moment: 'Pulled Joseph out of the pit and into Pharaoh\'s palace', reference: 'Genesis 37 & 41' },
      { id: 'h5', moment: 'Led Israel out of Egypt with a mighty hand', reference: 'Exodus 13:9' },
      { id: 'h6', moment: 'Parted the Red Sea', reference: 'Exodus 14:21' },
      { id: 'h7', moment: 'Guided Joshua into the Promised Land', reference: 'Joshua 1:5' },
      { id: 'h8', moment: 'Anointed David from the sheep field to the throne', reference: '1 Samuel 16:13' },
      { id: 'h9', moment: 'Raised Lazarus from the grave', reference: 'John 11:43-44' },
      { id: 'h10', moment: 'Was pierced for our transgressions on the Cross', reference: 'Isaiah 53:5; John 20:25-27' },
      { id: 'h11', moment: 'Is reaching for YOU — today', reference: 'Hebrews 13:8' }
    ];

    return (
      <>
        {/* Overview */}
        <StudySection id="overview" title="Scripture Foundation & Setting">
          <div className="space-y-4">
            <div className="bg-yellow-400 text-black p-4 rounded-lg">
              <h4 className="font-bold mb-2">Mark 8:22-25 (KJV)</h4>
              <p className="italic mb-2">
                "And he cometh to Bethsaida; and they bring a blind man unto him, and besought him to touch him.
                And he took the blind man by the hand, and led him out of the town; and when he had spit on his eyes,
                and put his hands upon him, he asked him if he saw ought.
              </p>
              <p className="italic mb-2">
                And he looked up, and said, I see men as trees, walking.
              </p>
              <p className="italic">
                After that he put his hands again upon his eyes, and made him look up: and he was restored, and saw every man clearly."
              </p>
            </div>
            <p className="text-gray-300">
              <strong className="text-yellow-400">Prophet Sumpter preaches this text "backwards"</strong> — beginning with the man, then the city,
              then the touch, and finally the Hand itself. The revelation: <em>The Hand that healed him is the SAME Hand that has worked through every page of Scripture — and the SAME Hand is reaching for you today.</em>
            </p>
            <div className="bg-gray-800 p-4 rounded-lg">
              <h5 className="text-yellow-400 font-semibold mb-2">Opening Reflection:</h5>
              <p className="text-gray-300">When you read this story, do you focus on the eyes that were healed, or the Hand that did the healing? What changes when you shift your gaze from the miracle to the One performing it?</p>
            </div>
          </div>
        </StudySection>

        {/* The Man of Darkness */}
        <StudySection id="man-of-darkness" title="The Man of Darkness: An Anonymous Struggle">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800 p-4 rounded-lg">
                <h5 className="text-yellow-400 font-bold mb-2">What We Don't Know</h5>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• His name</li>
                  <li>• His hometown</li>
                  <li>• How long he had been blind</li>
                  <li>• How he made a living</li>
                  <li>• Whether he was born blind or lost his sight</li>
                </ul>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg">
                <h5 className="text-yellow-400 font-bold mb-2">What We DO Know</h5>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• He could not find Jesus on his own</li>
                  <li>• <em>Somebody brought him</em> to Jesus</li>
                  <li>• He had learned to survive in the dark</li>
                  <li>• He didn't beg like Bartimaeus — he just trusted</li>
                </ul>
              </div>
            </div>
            <div className="bg-gradient-to-r from-purple-900 to-blue-900 p-4 rounded-lg">
              <p className="text-white italic">
                <strong className="text-yellow-400">Romero's words:</strong> "Not because he found Jesus — but because somebody brought him to Jesus."
              </p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg">
              <h5 className="text-yellow-400 font-semibold mb-2">Reflection Prompt:</h5>
              <p className="text-gray-300">Who brought YOU to Jesus? Whose hand are you holding today that you should be leading to Him?</p>
            </div>
          </div>
        </StudySection>

        {/* Led Out of the City */}
        <StudySection id="led-out" title="Led Out of the City: Bethsaida the Cursed">
          <div className="space-y-4">
            <p className="text-gray-300">
              Before Jesus healed him, He had to <strong className="text-yellow-400">take him out of Bethsaida</strong>.
              Bethsaida was the city Jesus would later curse for its unbelief (Matthew 11:21).
              The miracle could not happen <em>inside</em> a place that refused to believe.
            </p>
            <div className="border border-yellow-400/40 rounded-lg p-4 bg-black/40">
              <div className="flex items-center space-x-3 mb-3">
                <Hand className="w-6 h-6 text-yellow-400" />
                <h5 className="text-yellow-400 font-bold">The Same Hand That Pulls You OUT…</h5>
              </div>
              <p className="text-gray-300 mb-2">
                Jesus did not heal him in the crowd. He took him by the hand and walked him out — past the noise, past the unbelief,
                past the familiar — into a private place where revelation could happen.
              </p>
              <p className="text-gray-300">
                <strong>God will not always heal you where you got sick.</strong> Sometimes the first miracle is the journey out.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800 p-4 rounded-lg">
                <h5 className="text-yellow-400 font-semibold mb-2">Matthew 11:21</h5>
                <p className="text-gray-300 text-sm italic">"Woe unto thee, Bethsaida! for if the mighty works… had been done in Tyre and Sidon, they would have repented…"</p>
              </div>
              <div className="bg-gray-800 p-4 rounded-lg">
                <h5 className="text-yellow-400 font-semibold mb-2">Discussion Question</h5>
                <p className="text-gray-300 text-sm">What "city" — environment, relationship, mindset, habit — is God currently asking you to step out of so that He can do something new?</p>
              </div>
            </div>
          </div>
        </StudySection>

        {/* The Twofold Touch */}
        <StudySection id="twofold-touch" title="The Twofold Touch: Vision, Then Sight">
          <div className="space-y-4">
            <p className="text-gray-300">
              This is the only recorded miracle of Jesus that happens in <strong className="text-yellow-400">two stages</strong>.
              That is not failure — it is <em>intentional</em>. Jesus is teaching us how God heals.
            </p>
            <div className="space-y-4">
              {twoTouches.map((touch) => (
                <div key={touch.id} className="border border-gray-700 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    {touch.icon}
                    <h4 className="text-yellow-400 font-bold text-lg">{touch.label}</h4>
                    <span className="text-xs text-gray-400 uppercase tracking-widest">{touch.verse}</span>
                  </div>
                  <p className="text-gray-300 mb-2"><strong className="text-white">What happened:</strong> {touch.what}</p>
                  <p className="text-gray-300 mb-2"><strong className="text-white">Result:</strong> {touch.result}</p>
                  <p className="text-yellow-400 italic">{touch.revelation}</p>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-r from-yellow-900/40 to-yellow-700/20 border border-yellow-400/40 p-4 rounded-lg">
              <h5 className="text-yellow-400 font-bold mb-2">Key Revelation</h5>
              <p className="text-white">
                <strong>"I see men as trees walking"</strong> is not a failed healing — it is a Psalm 1 picture.
                The wicked are like <em>walking trees</em> — uprooted, aimless. The righteous are <em>planted</em> by streams of water.
                Vision came first, so he could distinguish the planted from the wandering. Then sight came, and he could love them clearly.
              </p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg">
              <h5 className="text-yellow-400 font-semibold mb-2">Reflection Prompt:</h5>
              <p className="text-gray-300">Where in your life are you between the first touch and the second? What "blurry" thing is God asking you to trust Him with until clarity comes?</p>
            </div>
          </div>
        </StudySection>

        {/* The Same Hand Timeline */}
        <StudySection id="same-hand-timeline" title="The Same Hand: A Journey Through Scripture">
          <div className="space-y-4">
            <p className="text-gray-300">
              <strong className="text-yellow-400">The hand that touched the blind man's eyes is the SAME hand that:</strong>
            </p>
            <div className="relative pl-6">
              <div className="absolute left-2 top-2 bottom-2 w-px bg-gradient-to-b from-yellow-400 via-yellow-400/40 to-transparent" />
              <div className="space-y-3">
                {sameHandTimeline.map((moment, idx) => (
                  <div key={moment.id} className="relative">
                    <div className="absolute -left-[18px] top-2 w-3 h-3 rounded-full bg-yellow-400 ring-2 ring-yellow-400/30" />
                    <div className="bg-gray-800 rounded-lg p-3 ml-2">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <p className="text-white">
                          <span className="text-yellow-400 font-bold mr-2">{idx + 1}.</span>
                          {moment.moment}
                        </p>
                        <span className="text-xs text-yellow-400/80 whitespace-nowrap">{moment.reference}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-yellow-400 text-black p-4 rounded-lg">
              <p className="font-bold italic">"I serve a God that never fails. It's the same hand."</p>
            </div>
          </div>
        </StudySection>

        {/* The Cross */}
        <StudySection id="the-cross" title="The Ultimate Hand: The Cross">
          <div className="space-y-4">
            <p className="text-gray-300">
              The same Hand that formed Adam, parted the Red Sea, anointed David, and touched the blind man's eyes —
              <strong className="text-yellow-400"> chose to be pierced</strong>.
            </p>
            <div className="border border-yellow-400/40 rounded-lg p-4 bg-black/40">
              <h5 className="text-yellow-400 font-bold mb-2">John 20:27</h5>
              <p className="text-gray-300 italic">
                "Then saith he to Thomas, Reach hither thy finger, and behold my hands… and be not faithless, but believing."
              </p>
            </div>
            <p className="text-gray-300">
              The nail prints in Jesus' hands are the receipt of your healing. The Hand still bears the proof.
              When He reaches for you today, He reaches with the SAME hand that paid for you.
            </p>
            <div className="bg-gray-800 p-4 rounded-lg">
              <h5 className="text-yellow-400 font-semibold mb-2">Discussion Question:</h5>
              <p className="text-gray-300">If the same Hand that bore your sin is the Hand reaching for you today, what is keeping you from grabbing hold?</p>
            </div>
          </div>
        </StudySection>

        {/* Application */}
        <StudySection id="application" title="Hold The Hand: Application & Prayer">
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-purple-900 to-blue-900 p-4 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <Hand className="w-6 h-6 text-yellow-400" />
                <h4 className="text-yellow-400 font-bold">The Call to Action</h4>
              </div>
              <p className="text-white italic">"Do you know whose hand you're holding? God is holding your hand. Hold the hand."</p>
            </div>

            <div className="bg-gray-800 p-4 rounded-lg">
              <h5 className="text-yellow-400 font-bold mb-3">This Week's Challenge:</h5>
              <ul className="space-y-2 text-gray-300">
                <li>• <strong>Identify your Bethsaida</strong> — name one place, habit, or relationship Jesus is leading you OUT of</li>
                <li>• <strong>Trust the first touch</strong> — thank God for the vision He's already given, even if it's still blurry</li>
                <li>• <strong>Stay close for the second touch</strong> — don't walk away half-healed; let Him finish the work</li>
                <li>• <strong>Bring someone to Jesus</strong> this week — be the friend who led the blind man to the Master</li>
                <li>• <strong>Remember the Hand</strong> — every morning, declare: "The SAME Hand that ____ is the same Hand on my life today."</li>
              </ul>
            </div>

            <div className="bg-yellow-400 text-black p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Heart className="w-5 h-5" />
                <h5 className="font-bold">Closing Prayer:</h5>
              </div>
              <p className="italic">
                "Father, I thank You that the same Hand that formed the world is the Hand on my life today.
                The same Hand that pulled Joseph out of the pit, parted the Red Sea, and was pierced on Calvary —
                is the Hand reaching for me right now. I receive the first touch and I trust You for the second.
                Lead me out of every Bethsaida and into the clarity of Your presence. I will not let go of Your Hand. In Jesus' name, Amen."
              </p>
            </div>
          </div>
        </StudySection>
      </>
    );
  };

  // ============================================
  // Sermon Metadata & Routing
  // ============================================
  const sermonMeta = {
    1: {
      title: 'He Waits at the Gates — Interactive Study',
      subtitle: 'Psalm 100:4 | Thanksgiving Day Service',
      speaker: 'Prophet Romero Q. Sumpter Sr.',
      church: 'L!VE Church',
      date: 'November 27, 2025'
    },
    2: {
      title: 'The Same Hand — Interactive Study',
      subtitle: 'Mark 8:22-25 | The Healing of the Blind Man at Bethsaida',
      speaker: 'Prophet Romero Q. Sumpter Sr.',
      church: 'Encounter Kingdom Glory Network',
      date: ''
    }
  };

  const meta = sermonMeta[sermonId] || sermonMeta[1];
  const renderContent = sermonId === 2 ? renderTheSameHand : renderHeWaitsAtTheGates;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" data-testid="study-guide-modal">
      <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 px-6 py-4 border-b border-gray-700 rounded-t-lg z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-yellow-400">{meta.title}</h2>
              <p className="text-gray-300">{meta.subtitle}</p>
              <p className="text-gray-400 text-sm">
                {meta.speaker}
                {meta.church && ` | ${meta.church}`}
                {meta.date && ` | ${meta.date}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl font-bold"
              data-testid="study-guide-close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default StudyGuide;
